<template>
    <v-container style="max-width: 100%">
        <v-row class="text-center">
            <v-text-field
                class="mx-4 my-2"
                data-testId="classSessionId"
                v-model.trim="classSessionId"
                label="Class Session id#"
                variant="underlined"
                style="max-width: 250px"
                :disabled="sessionIdSet"
            ></v-text-field>
            <v-select
                v-if="sessionIdSet && studentsLoaded && !studentsListLoading"
                data-testId="studentId"
                v-model="studentId"
                :items="studentsList"
                width="150"
                label="Select Student id#"
                variant="underlined"
                class="mx-4 my-2"
                style="max-width: 200px"
                @update:model-value="loadData()"
            ></v-select>
            <v-progress-circular v-if="studentsListLoading" indeterminate color="primary" class="mx-2 my-4"></v-progress-circular>
            <v-btn color="primary" class="mx-4 my-4" @click="loadStudents()" v-if="!studentsLoaded && !studentsListLoading && !classStatsloading"> LOAD STUDENTS</v-btn>
            <v-btn color="primary" class="mx-4 my-4" @click="resetUI()" v-if="sessionIdSet && studentsLoaded"> RESET </v-btn>
            <v-btn color="primary" class="mx-4 my-4" @click="getClassAccuracy()" v-if="!classStatsloading"> GET CLASS ACCURACY</v-btn>
            <v-progress-circular class="mx-4 my-4 title-element" v-if="classStatsloading" indeterminate color="primary"></v-progress-circular>
            <span class="mx-2 my-5" v-if="classStatsloading">Loading class statistics</span>
        </v-row>

        <v-data-table
            v-if="sessionIdSet && studentId !== ''"
            data-testId="sessionsData"
            :headers="headers"
            :items="tableData"
            item-value="id"
            class="elevation-1 table-striped"
            :sort-by="[{ key: 'time', order: 'asc' }]"
            :loading="tableDataLoading"
            loading-text="Loading... Please wait"
            :page="page"
            :items-per-page="itemsPerPage"
            hide-default-footer
            show-expand
            :expanded.sync="expanded"
        >
            <template v-slot:top>
                <v-toolbar flat>
                    <v-toolbar-title v-if="sessionIdSet && studentsLoaded && !tableDataLoading">
                        {{ studentStats.accuracy }}% accurate from {{ studentStats.totalRecords - studentStats.notReviewed }} out of {{ tableData ? tableData.length : 0 }} records
                    </v-toolbar-title>

                    <v-toolbar-title style="text-align: right" class="mx-3">Filter: </v-toolbar-title>
                    <v-toolbar-items>
                        <v-select
                            data-testId="requestType"
                            v-model="filter.requestType"
                            :items="requestTypes"
                            item-title="text"
                            item-value="value"
                            width="150"
                            label="Request Type"
                            variant="underlined"
                            class="mx-2 my-2"
                            style="width: 150px"
                            @update:model-value="filterResults()"
                        ></v-select>

                        <v-select
                            data-testId="aiClassification"
                            v-model="filter.aiClassification"
                            :items="aiClassificationsList"
                            item-title="text"
                            item-value="value"
                            width="150"
                            label="AI Classification"
                            variant="underlined"
                            class="mx-2 my-2"
                            style="width: 150px"
                            @update:model-value="filterResults()"
                        ></v-select>

                        <v-select
                            data-testId="humanClassification"
                            v-model="filter.humanClassification"
                            :items="humanClassificationsList"
                            item-title="text"
                            item-value="value"
                            width="150"
                            label="Human Classification"
                            variant="underlined"
                            class="mx-2 my-2"
                            style="width: 150px"
                            @update:model-value="filterResults()"
                        ></v-select>

                        <v-select
                            data-testId="captureModesFilter"
                            v-model="filter.captureMode"
                            :items="captureModes"
                            item-title="text"
                            item-value="value"
                            width="150"
                            label="Capture Mode"
                            variant="underlined"
                            class="mx-2 my-2"
                            style="width: 150px"
                            @update:model-value="filterResults()"
                        ></v-select>

                        <v-btn
                            color="primary"
                            class="mx-2"
                            @click="clearSearch()"
                            variant="text"
                            v-if="filter.requestType !== '' || filter.aiClassification !== '' || filter.humanClassification !== '' || filter.captureMode !== ''"
                        >
                            CLEAR SEARCH
                        </v-btn>
                    </v-toolbar-items>
                </v-toolbar>
            </template>
            <template v-slot:expanded-row="{ columns, item }">
                <td :colspan="columns.length" style="padding: 20px">
                    <v-container class="bg-grey-lighten-5">
                        <v-row no-gutters>
                            <v-col cols="2">
                                <span class="item-label">Objective</span>
                                <span class="item-value">{{ item.objectiveText }}</span>
                                <br />
                            </v-col>
                            <v-col cols="5">
                                <span class="item-label">
                                    Tabs
                                    <v-chip
                                        :color="getColorCodeForSimilarityValue(item.avgTabSimilarity)"
                                        variant="outlined"
                                        size="small"
                                        class="mx-2"
                                        v-if="item.avgTabSimilarity"
                                    >
                                        {{ item.avgTabSimilarity }}
                                        <v-tooltip activator="parent" location="end"
                                            >Average Tab Similarity Score ({{
                                                displayNameForClassification(getClassificationBasedOnSimilarityScore(item.avgTabSimilarity))
                                            }})</v-tooltip
                                        >
                                    </v-chip>
                                </span>
                                <ul v-for="tab in item.tabsInfo" :key="tab" class="my-2">
                                    <li>
                                        <v-icon icon="mdi-message-draw" :color="tab.hasClassification ? 'green' : 'grey'" @click="openTabClassificationDialog(tab, item)" />
                                        &nbsp;&nbsp;
                                        <span @click="showOpenLinkConfirmDialog(tab.url)" class="docker-log-link text-primary" style="text-decoration: underline; cursor: pointer">
                                            {{ tab.cleanTitle }}
                                        </span>
                                        <v-chip
                                            :color="getColorCodeForSimilarityValue(tab.tabSimilarity)"
                                            variant="outlined"
                                            size="small"
                                            class="mx-2"
                                            density="compact"
                                            v-if="tab.tabSimilarity"
                                        >
                                            {{ tab.tabSimilarity }}
                                            <v-tooltip activator="parent" location="end"
                                                >Tab Similarity Score ({{ displayNameForClassification(getClassificationBasedOnSimilarityScore(tab.tabSimilarity)) }})</v-tooltip
                                            >
                                        </v-chip>
                                        <v-chip color="primary" size="x-small" v-if="tab.isActive">Active</v-chip>
                                        <v-chip color="red" size="x-small" v-if="tab.usedDataGathering === true">Used Data Gathering</v-chip>
                                        <div class="item-value-small">{{ getDomain(tab.url) }}</div>
                                    </li>
                                </ul>
                            </v-col>
                            <v-col cols="5" style="position: relative">
                                <span class="item-label">
                                    OCR Text
                                    <v-chip :color="getColorCodeForSimilarityValue(item.textSimilarity)" variant="outlined" size="small" class="mx-2" v-if="item.textSimilarity">
                                        {{ item.textSimilarity }}
                                        <v-tooltip activator="parent" location="end"
                                            >Text Similarity Score ({{ displayNameForClassification(getClassificationBasedOnSimilarityScore(item.textSimilarity)) }})</v-tooltip
                                        >
                                    </v-chip>
                                </span>
                                <br />
                                <span>
                                    {{ item.ocrText }}
                                </span>
                                <br />
                                <br />
                                <span class="item-label" v-if="item.usedDataGathering"> Active Tab contents </span>
                                <br />
                                <span v-if="item.usedDataGathering">
                                    {{ item.activeTabText }}
                                </span>
                                <span v-if="item.requestType === 'image' || item.usedDataGathering" class="d-block mt-2"></span>
                                <span v-if="item.requestType === 'image' || item.usedDataGathering" class="d-block mt-2">
                                    <SessionImageViewer :path="item.path"></SessionImageViewer>
                                </span>
                                <v-icon class="deepLink" icon="mdi-link" @click="copyDeepLink($event, item)" title="Copy Direct Link" :class="{ highlighted: item.copied }" />
                            </v-col>
                        </v-row>
                        <hr class="my-4" />
                        <v-row no-gutters>
                            <v-col cols="3">
                                <v-select
                                    :items="humanClassificationsList"
                                    item-title="text"
                                    item-value="value"
                                    label="Human Classification"
                                    v-model="item.humanClassification"
                                    @update:model-value="OnHumanClassificationUpdated(item)"
                                    variant="underlined"
                                    class="mx-2 my-2"
                                ></v-select>
                            </v-col>
                            <v-col cols="3">
                                <v-select
                                    :items="confidenceItems"
                                    item-title="text"
                                    item-value="value"
                                    label="Confidence"
                                    v-model="item.confidence"
                                    @update:model-value="OnHumanClassificationUpdated(item)"
                                    variant="underlined"
                                    class="mx-2 my-2"
                                ></v-select>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field
                                    label="Notes"
                                    v-model="item.notes"
                                    variant="underlined"
                                    multi-line
                                    class="capitalize mx-2 my-2"
                                    @blur="OnHumanClassificationUpdated(item)"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="2">
                                <v-text-field label="Reviewer" :disabled="true" v-model="item.reviewer" variant="underlined" class="capitalize mx-2 my-2"></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </td>
            </template>

            <template v-slot:[`item.time`]="{ item }">{{ formatTime(item.time) }}</template>

            <template v-slot:[`item.requestType`]="{ item }">{{ displayNameForRequestType(item.requestType, item.usedDataGathering) }}</template>

            <template v-slot:[`item.finalSimilarity`]="{ item }">
                <v-chip :color="getColorCodeForSimilarityValue(item.finalSimilarity)" variant="tonal" size="small" class="mx-2" v-if="item.finalSimilarity">
                    {{ item.finalSimilarity }}
                    <v-tooltip activator="parent" location="end"
                        >Final Similarity Score ({{ displayNameForClassification(getClassificationBasedOnSimilarityScore(item.finalSimilarity)) }})</v-tooltip
                    >
                </v-chip>
            </template>

            <template v-slot:[`item.aiClassification`]="{ item }">{{ displayNameForClassification(item.aiClassification) }}</template>

            <template v-slot:[`item.humanClassification`]="{ item }">{{ displayNameForClassification(item.humanClassification) }}</template>

            <template v-slot:[`item.reviewed`]="{ item }">
                <span v-if="item.hasReviews" tool>
                    <v-icon icon="mdi-message-draw" color="green" />
                    <v-tooltip activator="parent" location="bottom">Reviewed</v-tooltip>
                </span>
            </template>

            <template v-slot:[`item.accuracy`]="{ item }">
                <span v-if="item.classificationMatched === undefined">
                    <span v-if="!item.humanClassification && item.aiClassification === Classifications.POSSIBLY_OFF_TASK">
                        <v-icon icon="mdi-check-circle" color="orange-lighten-1" />
                    </span>
                </span>
                <span v-if="item.classificationMatched === false">
                    <v-icon icon="mdi-close-circle" color="red" />
                </span>
                <span v-if="item.classificationMatched === true">
                    <v-icon icon="mdi-check-circle" color="green" />
                </span>
            </template>

            <template v-slot:bottom>
                <v-toolbar>
                    <v-row style="padding-top: 20px">
                        <v-col cols="9">
                            <v-pagination v-model="page" :length="pageCount"></v-pagination>
                        </v-col>
                        <v-col cols="3">
                            <v-select
                                :items="pagingOptions"
                                label="Page Size"
                                v-model="itemsPerPage"
                                class="mx-2"
                                @update:model-value="paginationChanged()"
                                variant="underlined"
                            ></v-select>
                        </v-col>
                    </v-row>
                </v-toolbar>
            </template>
        </v-data-table>
        <v-snackbar v-model="hasError" color="red" location="top" :multi-line="true">
            {{ errorMessage }}
            <v-btn variant="text" class="mx-2" @click="dismissError()">Dismiss</v-btn>
        </v-snackbar>

        <v-dialog scrollable width="80%" style="max-height: 80%; min-height: 80%" v-model="showAccuracyDialog">
            <template v-slot:default="{ isActive }">
                <v-card :title="'Class Accuracy (Session id# ' + classSessionId + ')'">
                    <v-card-text>
                        <v-data-table
                            data-testId="accuracyReportData"
                            :headers="accuracyReportHeaders"
                            :items="accuracyReportTableData"
                            item-value="studentId"
                            class="elevation-1 table-striped"
                            :sort-by="[{ key: 'accuracy', order: 'desc' }]"
                        >
                            <template v-slot:top>
                                <v-toolbar flat>
                                    <v-toolbar-title>
                                        {{ classStats.accuracy }}% accurate from {{ classStats.totalRecords - classStats.notReviewed }} out of {{ classStats.totalRecords }} records
                                        from {{ accuracyReportTableData.length }} students
                                    </v-toolbar-title>
                                </v-toolbar>
                            </template>

                            <template v-slot:[`item.studentId`]="{ item }"
                                ><a
                                    class="text-decoration-none"
                                    href="#"
                                    @click="
                                        isActive.value = false;
                                        showStudentRecordsFromAccuracyReport(item.studentId);
                                    "
                                    style="cursor: pointer"
                                >
                                    <strong> {{ item.studentId }} </strong>
                                </a></template
                            >

                            <template v-slot:[`item.accuracy`]="{ item }">{{ item.accuracy }} %</template>
                        </v-data-table>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn variant="text" @click="isActive.value = false">Close Dialog</v-btn>
                    </v-card-actions>
                </v-card>
            </template>
        </v-dialog>
        <v-dialog scrollable width="80%" style="max-height: 80%; min-height: 80%" v-model="showOpenLinkConfirmationDialog">
            <template v-slot:default="{ isActive }">
                <v-card :title="'Open Link Confirmation Dialog'">
                    <v-card-text> Open Link : {{ currentClickedLink }} </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                            variant="text"
                            @click="
                                openCurrentLinkInNewWindow();
                                isActive.value = false;
                            "
                            color="primary"
                            >Open Link</v-btn
                        >
                        <v-btn variant="text" @click="isActive.value = false">Cancel</v-btn>
                    </v-card-actions>
                </v-card>
            </template>
        </v-dialog>

        <v-dialog scrollable width="80%" style="max-height: 80%; min-height: 80%" v-model="showTabClassificationDialog">
            <template v-slot:default="{ isActive }">
                <v-card :title="currentClickedTab.url">
                    <v-card-text>
                        <h4 class="text-red">Note: Beacuse of PII information contained in tab title and contents it will not be stored in database.</h4>
                        <hr class="my-4" />
                        <v-row no-gutters>
                            <v-col cols="3">
                                <strong>Class Objective</strong>
                                <br />
                                {{ currentClickedTab.objectiveText }}
                                <br />
                                <br />
                                <strong>Is Active</strong>
                                <br />
                                {{ currentClickedTab.isActive }}
                                <br />
                                <br />
                                <strong>Similarity Score</strong>
                                <br />
                                <v-chip
                                    :color="getColorCodeForSimilarityValue(currentClickedTab.tabSimilarity)"
                                    variant="outlined"
                                    size="small"
                                    v-if="currentClickedTab.tabSimilarity"
                                >
                                    {{ currentClickedTab.tabSimilarity }}
                                    <v-tooltip activator="parent" location="end">{{
                                        displayNameForClassification(getClassificationBasedOnSimilarityScore(currentClickedTab.tabSimilarity))
                                    }}</v-tooltip>
                                </v-chip>
                            </v-col>
                            <v-col cols="3">
                                <strong>Title <span class="text-red">(PII's Removed)</span></strong>
                                <br />
                                <v-textarea
                                    class="mx-4 my-2"
                                    data-testId="tabtitle"
                                    v-model="currentClickedTab.cleanTitleUpdated"
                                    label="Tab title"
                                    @blur="OnTabClassificationUpdated(currentClickedTab)"
                                ></v-textarea>
                            </v-col>
                            <v-col cols="6">
                                <strong>Content <span class="text-red">(PII's Removed)</span></strong>
                                <br />
                                <v-textarea
                                    class="mx-4 my-2"
                                    data-testId="tabContent"
                                    v-model="currentClickedTab.contentUpdated"
                                    label="Tab Content"
                                    @blur="OnTabClassificationUpdated(currentClickedTab)"
                                ></v-textarea>
                            </v-col>
                        </v-row>
                        <hr class="my-4" />
                        <v-row no-gutters>
                            <v-col cols="3">
                                <v-select
                                    :items="humanClassificationsList"
                                    item-title="text"
                                    item-value="value"
                                    label="Human Classification"
                                    v-model="currentClickedTab.humanClassification"
                                    @update:model-value="OnTabClassificationUpdated(currentClickedTab)"
                                    variant="underlined"
                                    class="mx-2 my-2"
                                ></v-select>
                            </v-col>
                            <v-col cols="3">
                                <v-select
                                    :items="confidenceItems"
                                    item-title="text"
                                    item-value="value"
                                    label="Confidence"
                                    v-model="currentClickedTab.confidence"
                                    @update:model-value="OnTabClassificationUpdated(currentClickedTab)"
                                    variant="underlined"
                                    class="mx-2 my-2"
                                ></v-select>
                            </v-col>
                            <v-col cols="3">
                                <v-text-field
                                    label="Notes"
                                    v-model="currentClickedTab.notes"
                                    variant="underlined"
                                    multi-line
                                    class="capitalize mx-2 my-2"
                                    @blur="OnTabClassificationUpdated(currentClickedTab)"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="2">
                                <v-text-field
                                    label="Reviewer"
                                    :disabled="true"
                                    v-model="currentClickedTab.reviewer"
                                    variant="underlined"
                                    class="capitalize mx-2 my-2"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn variant="text" @click="isActive.value = false">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </template>
        </v-dialog>
    </v-container>
</template>

<script lang="ts">
import { RestService } from '../services/rest.service';
import baseComponent from './baseComponent';
import SessionImageViewer from './SessionImageViewer.vue';
import { Classifications, ClassificationColors, emailRegex, phoneRegex, ssnRegex, creditCardRegex, ipAddressRegex } from '../utils/config';
import * as commonUtils from '../utils/common';

export default {
    name: 'SessionsViewer',
    extends: baseComponent,
    components: { SessionImageViewer },
    data: () => ({
        page: 1,
        itemsPerPage: 10,
        pagingOptions: [5, 10, 15, 20, 50, 100, 500, 1000],
        tableDataLoading: true,
        requestTypes: [
            { text: 'Image Thumbnail', value: 'image' },
            { text: 'Text Data', value: 'text' },
            { text: 'Data Gathering', value: 'dataGathering' }
        ],
        captureModes: [
            { text: 'Full Screen', value: 'Full Screen' },
            { text: 'Active Tab', value: 'Active Tab' }
        ],
        Classifications: Classifications,
        ClassificationColors: ClassificationColors,
        aiClassificationsList: [
            { text: 'Focused', value: Classifications.FOCUSED },
            { text: 'On Task', value: Classifications.ON_TASK },
            { text: 'Possibly Off Task', value: Classifications.POSSIBLY_OFF_TASK },
            { text: 'Off Task', value: Classifications.OFF_TASK }
        ],
        humanClassificationsList: [
            { text: 'Focused', value: Classifications.FOCUSED },
            { text: 'On Task', value: Classifications.ON_TASK },
            { text: 'Possibly Off Task', value: Classifications.POSSIBLY_OFF_TASK },
            { text: 'Off Task', value: Classifications.OFF_TASK }
        ],
        confidenceItems: [
            { text: 'Low', value: 'Low' },
            { text: 'Medium', value: 'Medium' },
            { text: 'High', value: 'High' }
        ],
        classSessionId: '',
        sessionIdSet: false,
        studentsList: [],
        studentsListLoading: false,
        studentsLoaded: false,
        studentId: '',
        rawData: [],
        tableData: [],
        studentStats: {
            totalRecords: 0,
            correct: 0,
            wrong: 0,
            notReviewed: 0,
            accuracy: 0
        },
        classStats: {
            totalRecords: 0,
            correct: 0,
            wrong: 0,
            notReviewed: 0,
            accuracy: 0
        },
        currentClickedLink: '',
        currentClickedTab: {} as any,
        showOpenLinkConfirmationDialog: false,
        showTabClassificationDialog: false,
        classStatsloading: false,
        showAccuracyDialog: false,
        accuracyReportHeaders: [
            { title: 'Student id#', key: 'studentId' },
            { title: 'Total Records', key: 'totalRecords' },
            { title: 'Accuracy', key: 'accuracy' },
            { title: 'Correct', key: 'correct' },
            { title: 'Wrong', key: 'wrong' },
            { title: 'Not reviewed', key: 'notReviewed' }
        ],
        accuracyReportTableData: [],
        totalStudentsAccuracyReport: 0,
        filter: {
            captureMode: '',
            requestType: '',
            aiClassification: '',
            humanClassification: ''
        },
        errorMessage: null,
        expandTable: '',
        valid: false,
        restService: new RestService(),
        headers: [
            { title: '', key: 'data-table-expand' },
            { title: '', key: 'reviewed' },
            { title: 'Time', key: 'time' },
            { title: 'Request Type', key: 'requestType' },
            { title: 'Capture Mode', key: 'captureMode' },
            { title: 'Similarity analysis', key: 'finalSimilarity' },
            { title: 'AI Inference', key: 'aiClassification' },
            { title: 'Human Review', key: 'humanClassification' },
            { title: 'Accuracy', key: 'accuracy' }
        ],
        expanded: []
    }),
    created() {
        const lastLocalPageSize = localStorage.getItem('lastPageSize');
        const itemsPerPage = parseInt(lastLocalPageSize, 10);
        if (!isNaN(itemsPerPage)) {
            this.itemsPerPage = itemsPerPage;
        }
        //the delay allows the environment variable to initialize
        setTimeout(() => {
            this.loadDeepLink();
        }, 1000);
    },
    methods: {
        dismissError() {
            this.errorMessage = null;
        },
        resetUI() {
            this.sessionIdSet = false;
            this.clearOldData();
        },
        async loadStudents() {
            if (this.classSessionId) {
                this.studentsListLoading = true;
                try {
                    this.studentsList = await this.restService.getAllSessionStudents(this.environment, this.classSessionId);
                    if (this.studentsList.length === 0) {
                        this.setError(`No students found for class session id# ${this.classSessionId}`);
                        this.sessionIdSet = false;
                    } else {
                        this.sessionIdSet = true;
                        this.studentsLoaded = true;
                    }
                } catch (error) {
                    this.setError(`Error processing request: ${error.message}`);
                    this.studentsList = [];
                }
                this.studentsListLoading = false;
            } else {
                this.setError('Please provide a session id# to load students list.');
            }
        },

        async getClassAccuracy() {
            if (this.classSessionId) {
                try {
                    this.classStatsloading = true;
                    const accuracyStats = await this.restService.getClassAccuracy(this.environment, this.classSessionId);
                    this.classStats = {
                        ...accuracyStats.classStats
                    };
                    const studentsAccuracyReport = [];
                    for (const studentId of Object.keys(accuracyStats.studentStats)) {
                        studentsAccuracyReport.push({
                            studentId,
                            ...accuracyStats.studentStats[studentId]
                        });
                    }
                    this.accuracyReportTableData = studentsAccuracyReport;
                    this.classStatsloading = false;
                    this.showAccuracyDialog = true;
                } catch (error) {
                    this.showAccuracyDialog = false;
                    this.classStatsloading = false;
                    this.setError(`Error processing request: ${error.message}`);
                }
            } else {
                this.classStatsloading = false;
                this.showAccuracyDialog = false;
                this.setError('Please provide a session id# to load class accuracy.');
            }
        },
        async showStudentRecordsFromAccuracyReport(studentId) {
            this.studentId = studentId;
            this.loadData();
        },
        async fetchData() {
            try {
                this.rawData = await this.restService.getAllSessionDataLogs(this.environment, this.classSessionId, this.studentId);

                for (const item of this.rawData) {
                    const newTabsInfo = [];
                    let usedDataGathering = false;
                    for (const tab of item.tabsInfo) {
                        if (tab.usedDataGathering === true) {
                            usedDataGathering = true;
                            item.activeTabText = tab.content;
                        }
                        // Calculate AI classification based on similarity score & well defined classification thresholds
                        tab.aiClassification = commonUtils.getClassificationBasedOnSimilarityScore(tab.tabSimilarity);

                        if (item.tabsClassification) {
                            const tabClassification = item.tabsClassification.filter((tabClassification) => tabClassification.url === tab.url);
                            if (tabClassification && tabClassification.length !== 0) {
                                const cleanTitleUpdated = tabClassification[0].cleanTitle;
                                const contentUpdated = tabClassification[0].content;
                                delete tabClassification[0].cleanTitle;
                                delete tabClassification[0].content;
                                newTabsInfo.push({
                                    ...tab,
                                    ...tabClassification[0],
                                    cleanTitleUpdated,
                                    contentUpdated,
                                    hasClassification: true
                                });
                            } else {
                                newTabsInfo.push(tab);
                            }
                        } else {
                            newTabsInfo.push(tab);
                        }
                    }
                    item.tabsInfo = newTabsInfo;
                    item.usedDataGathering = usedDataGathering;

                    if (item.humanClassification || (item.tabsClassification && item.tabsClassification.length != 0)) {
                        item.hasReviews = true;
                    } else {
                        item.hasReviews = false;
                    }
                }
                this.filterResults();
                this.calculateAccuracyForStudent(this.tableData);
            } catch (error) {
                this.setError(`Error processing request: ${error.message}`);
                this.rawData = [];
            }
        },
        calculateAccuracyForStudent(tableData) {
            this.studentStats = {
                totalRecords: this.rawData.length,
                correct: 0,
                wrong: 0,
                notReviewed: 0,
                accuracy: 0
            };
            for (const item of tableData) {
                let hasClassification = false;
                let humanClassificationCorrect = true;
                let tabsClassificationCorrect = true;

                if (item.humanClassification) {
                    hasClassification = true;
                    if (item.humanClassification !== item.aiClassification) {
                        humanClassificationCorrect = false;
                    }
                }

                if (item.tabsInfo) {
                    const classifiedTabs = item.tabsInfo.filter((tab) => tab.humanClassification !== undefined);
                    if (classifiedTabs && classifiedTabs.length !== 0) {
                        hasClassification = true;
                        classifiedTabs.forEach((classifiedTab) => {
                            if (classifiedTab.humanClassification !== classifiedTab.aiClassification) {
                                tabsClassificationCorrect = false;
                            }
                        });
                    }
                }

                if (hasClassification) {
                    if (humanClassificationCorrect && tabsClassificationCorrect) {
                        this.studentStats.correct++;
                        item.classificationMatched = true;
                    } else {
                        this.studentStats.wrong++;
                        item.classificationMatched = false;
                    }
                } else {
                    this.studentStats.notReviewed++;
                }
            }

            if (this.studentStats.correct != 0 && this.studentStats.correct + this.studentStats.wrong !== 0) {
                this.studentStats.accuracy = Math.trunc((this.studentStats.correct / (this.studentStats.correct + this.studentStats.wrong)) * 100);
            }
        },
        refresh() {
            this.clearOldData();
            this.loadStudents();
        },
        paginationChanged() {
            localStorage.setItem('lastPageSize', this.itemsPerPage);
        },
        async loadData() {
            this.resetDatatableData();
            if (this.classSessionId && this.studentId) {
                this.sessionIdSet = true;
                this.tableDataLoading = true;
                await this.fetchData();
                this.filterResults();
                this.tableDataLoading = false;
            } else {
                this.setError('Please provide a session id# & select student id# to load sessions.');
            }
        },
        clearSearch() {
            this.filter.captureMode = '';
            this.filter.requestType = '';
            this.filter.aiClassification = '';
            this.filter.humanClassification = '';
            this.filterResults();
        },
        filterResults() {
            if (!this.rawData) {
                return;
            }
            this.tableData = this.rawData.filter((element) => {
                for (const [key, value] of Object.entries(this.filter)) {
                    if (value) {
                        if (key === 'requestType' && value === 'dataGathering') {
                            return element['usedDataGathering'];
                        } else if (['captureMode', 'requestType', 'aiClassification', 'humanClassification'].includes(key)) {
                            if (!element[key] || !element[key].toUpperCase().includes(value.toString().toUpperCase())) {
                                return false;
                            }
                        }
                    }
                }
                return true;
            });
        },
        clearOldData() {
            this.sessionIdSet = false;
            this.studentId = '';
            this.studentsLoaded = false;
            this.studentsList = [];
            this.resetDatatableData();
        },
        resetDatatableData() {
            this.rawData = [];
            this.tableData = [];
            this.page = 1;
        },
        async OnHumanClassificationUpdated(item) {
            const classificationObject = {
                environment: this.environment,
                operation: 'putClassification',
                classSessionId: this.classSessionId,
                studentId: this.studentId,
                filePath: item.path,
                classificationData: {
                    environment: this.environment,
                    objective: item.objectiveText,
                    textSimilarity: item.textSimilarity,
                    requestType: item.requestType,
                    aiClassification: item.aiClassification,
                    humanClassification: item.humanClassification,
                    confidence: item.confidence,
                    notes: item.notes,
                    reviewer: this.currentUser,
                    captureMode: item.captureMode,
                    createdOn: item.humanClassificationCreatedOn ? item.humanClassificationCreatedOn : Date.now(),
                    modifiedOn: Date.now()
                }
            };
            try {
                await this.restService.postHumanClassification(classificationObject);
                item.reviewer = this.currentUser;
                item.hasReviews = true;
                this.calculateAccuracyForStudent(this.tableData);
            } catch (err: any) {
                this.setError(err.message);
            }
        },
        async OnTabClassificationUpdated(tab: any) {
            const classificationObject = {
                environment: this.environment,
                operation: 'putTabClassification',
                classSessionId: this.classSessionId,
                studentId: this.studentId,
                filePath: tab.path,
                classificationData: {
                    environment: this.environment,
                    objective: tab.objectiveText,
                    url: tab.url,
                    // cleanTitle: tab.cleanTitleUpdated, // Disabled until legal review is done
                    cleanTitle: '',
                    tabSimilarity: tab.tabSimilarity,
                    aiClassification: tab.aiClassification,
                    // content: tab.contentUpdated ? tab.contentUpdated : undefined, // Disabled until legal review is done
                    content: '',
                    isActive: tab.isActive ? tab.isActive : false,
                    humanClassification: tab.humanClassification,
                    confidence: tab.confidence,
                    notes: tab.notes,
                    reviewer: this.currentUser,
                    captureMode: tab.captureMode,
                    createdOn: tab.createdOn ? tab.createdOn : Date.now(),
                    modifiedOn: Date.now()
                }
            };
            try {
                await this.restService.postTabClassification(classificationObject);
                tab.reviewer = this.currentUser;
                this.markTabAsClassified(tab.path, tab.url, classificationObject.classificationData);
                this.calculateAccuracyForStudent(this.tableData);
            } catch (err: any) {
                this.setError(err.message);
            }
        },
        markTabAsClassified(path, url, classificationData) {
            const records = this.tableData.filter((item) => item.path === path);
            for (const record of records) {
                const tabs = record.tabsInfo.filter((tab) => tab.url === url);
                for (const tab of tabs) {
                    // Copy saved classification details to local object in tabledata
                    tab.cleanTitleUpdated = classificationData.cleanTitle;
                    tab.contentUpdated = classificationData.content;
                    tab.humanClassification = classificationData.humanClassification;
                    tab.confidence = classificationData.confidence;
                    tab.aiClassification = classificationData.aiClassification;
                    tab.notes = classificationData.notes;
                    tab.reviewer = classificationData.reviewer;
                    tab.createdOn = classificationData.createdOn;
                    tab.modifiedOn = classificationData.modifiedOn;

                    // Mark tab as classified
                    tab.hasClassification = true;
                    // Mark record has reviews to true
                    record.hasReviews = true;
                }
            }
        },
        millisToMinutesAndSeconds(millis) {
            const minutes = Math.floor(millis / 60000);
            const seconds = ((millis % 60000) / 1000).toFixed(0);
            return minutes + ':' + (seconds == '10' ? '0' : '') + seconds;
        },
        displayNameForRequestType(type: string, usedDataGathering: boolean): string {
            if (usedDataGathering) {
                return 'Data Gathering Mode';
            } else {
                switch (type) {
                    case 'image':
                        return 'Image Thumbnail';
                    case 'text':
                        return 'Text data';
                    default:
                        return '-';
                }
            }
        },
        displayNameForClassification(type: string) {
            switch (type) {
                case Classifications.FOCUSED:
                    return 'Focused';
                case Classifications.ON_TASK:
                    return 'On Task';
                case Classifications.POSSIBLY_OFF_TASK:
                    return 'Possibly Off Task';
                case 'off-task':
                case Classifications.OFF_TASK:
                    return 'Off Task';
                default:
                    return '-';
            }
        },
        getColorCodeForSimilarityValue(similarityValue) {
            return commonUtils.getColorCodeForSimilarityValue(similarityValue);
        },
        getClassificationBasedOnSimilarityScore(similarityValue) {
            return commonUtils.getClassificationBasedOnSimilarityScore(similarityValue);
        },
        formatTime(time) {
            if (time) {
                return new Date(Math.trunc(time * 1000)).toLocaleString();
            } else {
                return '-';
            }
        },
        setError(message) {
            this.errorMessage = message;
            setTimeout(() => {
                this.errorMessage = null;
            }, 5000);
        },
        showOpenLinkConfirmDialog(url: string) {
            this.showOpenLinkConfirmationDialog = true;
            this.currentClickedLink = url;
        },
        openCurrentLinkInNewWindow() {
            this.showOpenLinkConfirmationDialog = false;
            window.open(this.currentClickedLink, '_blank');
        },
        openTabClassificationDialog(tab: any, item: any) {
            tab = {
                ...tab,
                cleanTitleUpdated: tab.cleanTitleUpdated && tab.cleanTitleUpdated != '' ? tab.cleanTitleUpdated : this.clearPII(tab.cleanTitle),
                contentUpdated: tab.contentUpdated && tab.contentUpdated != '' ? tab.contentUpdated : this.clearPII(this.getTabOriginalContents(tab, item)),
                objectiveText: item.objectiveText,
                path: item.path,
                captureMode: item.captureMode,
                humanClassification: tab.humanClassification ? tab.humanClassification : '',
                confidence: tab.confidence ? tab.confidence : '',
                notes: tab.notes ? tab.notes : '',
                reviewer: tab.reviewer ? tab.reviewer : this.currentUser
            };

            this.showTabClassificationDialog = true;
            this.currentClickedTab = tab;
        },
        getTabOriginalContents(tab, item) {
            if ((tab.isActive && item.requestType == 'image') || item.usedDataGathering) {
                if (item.ocrText) {
                    return item.ocrText;
                } else if (tab.content) {
                    return tab.content;
                }
            } else {
                return tab.content;
            }
        },
        clearPII(content: string) {
            if (content && content.length > 0) {
                content = content.replace(emailRegex, `[email removed]`);
                content = content.replace(phoneRegex, `[phone removed]`);
                content = content.replace(ssnRegex, `[SSN removed]`);
                for (const regex of creditCardRegex) {
                    content = content.replace(regex, `[credit card removed]`);
                }
                content = content.replace(ipAddressRegex, `[ip address removed]`);
            }
            return content;
        },
        getDomain(url) {
            try {
                const parsedUrl = new URL(url);
                return parsedUrl.hostname;
            } catch (e) {
                return '';
            }
        },
        copyDeepLink(evt, item) {
            item.copied = true;
            setTimeout(() => {
                item.copied = false;
            }, 1000);
            const link = location.host + `/#/${this.environment}/${this.classSessionId}/${this.studentId}/${item.time}`;
            navigator.clipboard.writeText(link);
        },
        async loadDeepLink() {
            const linkParts = location.hash.split('/');
            if (linkParts.length > 2) {
                const [env, classSessionId, studentId, time] = linkParts.slice(1);
                if (classSessionId) {
                    this.classSessionId = classSessionId;
                    await this.loadStudents();
                    if (studentId) {
                        this.studentId = studentId;
                        await this.loadData();
                        if (time) {
                            const sortByTimeAscItems = this.tableData.slice(0).sort((a, b) => (a.time < b.time ? -1 : 1));
                            const idx = sortByTimeAscItems.findIndex((item) => item.time === parseInt(time));
                            if (idx !== -1) {
                                const page = Math.floor(idx / this.itemsPerPage) + 1;
                                this.page = page;
                                this.expanded.push(sortByTimeAscItems[idx].id);
                            } else {
                                console.warn('Unable to find student sample with timestamp of ' + time);
                            }
                        }
                    }
                }
            }
        }
    },
    computed: {
        pageCount() {
            return Math.ceil(this.tableData.length / this.itemsPerPage);
        },
        hasError: function () {
            return this.errorMessage != null;
        }
    }
};
</script>

<style lang="scss">
.item-label {
    font-weight: bold;
}
.item-value {
    display: block !important;
}
.item-value-small {
    font-size: 10px;
    margin-left: 34px;
    height: 7px;
    line-height: 7px;
    color: #666666;
}

.deepLink {
    position: absolute !important;
    top: 0px;
    right: 0px;
}

.deepLink.highlighted {
    animation: scale-up 0.5s ease forwards;
    color: blue;
}

@keyframes scale-up {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.5);
    }
    100% {
        transform: scale(1);
    }
}
</style>
